import React, { useMemo, useState } from "react";
import {
  FORMER_MEMBER_LABEL,
  JOB_LIST_STATUS_FILTERS,
  filterJobListRows,
  type JobListStatusFilterId,
} from "@toolkit/mobile-shared";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { ClickableTableRow } from "../../../components/dashboard/ClickableTableRow";
import { JobListMediaCounts } from "../../../components/dashboard/JobListMediaCounts";
import { JobListStatusChip } from "../../../components/dashboard/JobListStatusChip";
import {
  ProductListEmptyFiltersMessage,
  ProductListFilterField,
  ProductListToolbar,
  PRODUCT_LIST_SEARCH_PLACEHOLDER,
} from "../../../components/dashboard/ProductListToolbar";
import { ButtonLink } from "../../../components/ui/button";
import { Card, CardHeader, CardTitle } from "../../../components/ui/card";
import { SelectInput } from "../../../components/ui/FormControls";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { dashboardJobHref } from "../../../lib/dashboardJobHref";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import type { JobListItem, MainAppPageProps } from "../types";

type JobsListPageProps = Pick<MainAppPageProps, "routes" | "canCreateJobs"> & {
  jobs?: JobListItem[];
  showTeamColumns?: boolean;
};

export const JobsListPage: React.FC<JobsListPageProps> = ({
  routes,
  jobs = [],
  canCreateJobs = false,
  showTeamColumns = false,
}) => {
  const newJobPath = routes.newDashboardJob ?? "/dashboard/jobs/new";
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<JobListStatusFilterId>("all");

  const filtered = useMemo(
    () => filterJobListRows(jobs, { query, status }),
    [jobs, query, status],
  );

  return (
    <DashboardPageLayout
      title="Jobs"
      headerActions={
        canCreateJobs ? (
          <ButtonLink to={newJobPath} variant="premium">
            Add a job site
          </ButtonLink>
        ) : undefined
      }
    >
      {jobs.length === 0 ? (
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-base">No job sites yet</CardTitle>
          </CardHeader>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          <ProductListToolbar
            search={{
              value: query,
              onChange: setQuery,
              placeholder: PRODUCT_LIST_SEARCH_PLACEHOLDER.jobs,
              ariaLabel: "Search job sites",
            }}
            filters={
              <ProductListFilterField id="job-status-filter" label="Status">
                <SelectInput
                  id="job-status-filter"
                  compact
                  className="h-9 min-w-[10rem]"
                  value={status}
                  onChange={(event) =>
                    setStatus(event.target.value as JobListStatusFilterId)
                  }
                  aria-label="Filter by status"
                >
                  {JOB_LIST_STATUS_FILTERS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </SelectInput>
              </ProductListFilterField>
            }
          />
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Site</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Media</TableHead>
                  <TableHead>Status</TableHead>
                  {showTeamColumns ? <TableHead>Created by</TableHead> : null}
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((job) => (
                  <ClickableTableRow
                    key={job.id}
                    href={dashboardJobHref(routes, job.id)}
                    ariaLabel={`Open job site ${job.name}`}
                  >
                    <TableCell className="font-semibold text-foreground">
                      {job.name}
                    </TableCell>
                    <TableCell className="max-w-[14rem] truncate text-muted-foreground">
                      {job.address || "—"}
                    </TableCell>
                    <TableCell>
                      <JobListMediaCounts
                        photoCount={job.photoCount}
                        videoCount={job.videoCount}
                      />
                    </TableCell>
                    <TableCell>
                      <JobListStatusChip job={job} showActiveLabel />
                    </TableCell>
                    {showTeamColumns ? (
                      <TableCell className="text-muted-foreground">
                        {job.ownerName || FORMER_MEMBER_LABEL}
                      </TableCell>
                    ) : null}
                    <TableCell className="text-muted-foreground">
                      {job.createdAtDisplay
                        ? ssrFormattedDateOr(job.createdAtDisplay)
                        : "—"}
                    </TableCell>
                  </ClickableTableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {filtered.length === 0 ? <ProductListEmptyFiltersMessage /> : null}
        </div>
      )}
    </DashboardPageLayout>
  );
};
